import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { ArrowLeft, Loader2, Layers, Sparkles, Edit3, X, Plus, Trash2, Phone, Image as ImageIcon, Search, Globe2 } from 'lucide-react';
import { useBackButton } from '../hooks/useBackButton';
import {
  getFabricCollectionById,
  updateFabricCollection,
  deleteFabricCollection,
  getAvailableChildCollections,
  addCollectionAsChild,
  removeCollectionFromParent,
  updatePromoterFabricVisibility,
  updatePromotedCollectionVisibility,
} from '../api/fabricCollection.api';
import { getProducts } from '../api/product.api';
import FabricCatalogueView from './FabricCatalogueView';

const ConfirmModal = ({
  open,
  title,
  description,
  tone = 'danger', // 'danger' | 'neutral'
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  onCancel,
  onConfirm
}) => {
  if (!open) return null;
  const confirmClass =
    tone === 'danger'
      ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-300'
      : 'bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300';

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden">
        <div className="p-5">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description ? (
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">{description}</p>
          ) : null}
        </div>
        <div className="px-5 pb-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 rounded-lg text-white font-semibold transition-colors flex items-center gap-2 disabled:cursor-not-allowed ${confirmClass}`}
          >
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : null}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const isLowResThumbnailUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return /\/thumbnails\//i.test(url) || /-thumbnail\.(jpe?g|png|webp)(\?|#|$)/i.test(url);
};

const getFabricImage = (fabric) => {
  const candidates = [
    fabric?.images?.original?.s3Url,
    fabric?.images?.original?.url,
    fabric?.images?.original?.imgbbUrl,
    fabric?.image,
  ]
    .filter(Boolean)
    .map((u) => String(u).trim())
    .filter(Boolean);

  const fullRes = candidates.find((u) => !isLowResThumbnailUrl(u));
  return fullRes || candidates[0] || 'https://via.placeholder.com/300x300?text=No+Image';
};

function ThumbnailImg({ src, alt, className = '' }) {
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
    />
  );
}

const FabricPreviewCard = ({
  fabric,
  onOpen,
  onGenerate,
  onEdit,
  onDelete,
  onRemoveFromCollection,
  isRemovingFromCollection = false,
  onToggleVisibility,
  onTogglePromoterFabricVisibility,
  isVisibilityLoading = false,
  collectionIsPublic = false,
  readOnly = false,
  showReadOnlyHint = true,
  promoterFabricMode = false,
}) => {
  const variantCount = fabric.colorVariants?.length || 0;
  const phone = useMemo(() => {
    const value = fabric?.user?.phone || fabric?.user?.phoneNumber || fabric?.phone || fabric?.phoneNumber || fabric?.contactNumber;
    if (!value) return null;
    const trimmed = typeof value === 'string' ? value.trim() : String(value);
    return trimmed.length ? trimmed : null;
  }, [fabric]);
  const publicLink = useMemo(() => {
    if (!phone) return null;
    const normalized = phone.replace(/\D/g, '');
    if (!normalized) return null;
    return `https://fashdeck.com/${normalized}`;
  }, [phone]);

  return (
    <div
      className="group bg-white rounded-lg sm:rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 overflow-hidden"
    >
      <div
        className="relative aspect-square bg-gray-100 overflow-hidden cursor-pointer"
        onClick={() => onOpen?.(fabric)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen?.(fabric);
          }
        }}
      >
        <ThumbnailImg
          src={getFabricImage(fabric)}
          alt={fabric.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-indigo-600 text-white text-xs font-semibold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full shadow">
          Fabric
        </div>
        {variantCount > 0 && (
          <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-blue-500 text-white text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full shadow">
            +{variantCount}
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <Layers size={16} />
              <span>View Fabric Details</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4">
        <h4 className="font-semibold text-gray-800 mb-1 line-clamp-1 text-sm sm:text-base">{fabric.name}</h4>
        <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3 line-clamp-2">{fabric.description || 'No description available.'}</p>

        {phone && publicLink && !readOnly && (
          <a
            href={publicLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 text-xs sm:text-sm text-purple-600 font-medium mb-2 sm:mb-3 hover:text-purple-700 transition-colors"
          >
            <Phone size={12} className="sm:w-3 sm:h-3" />
            <span>{phone}</span>
          </a>
        )}

        {promoterFabricMode ? (
          <div
            className="flex items-center justify-between mb-2 sm:mb-3 text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col min-w-0 pr-2">
              <span className="text-gray-500 font-medium">Show in my promoted catalogue</span>
              <span className="text-[10px] text-gray-400">
                {fabric.isPublic
                  ? 'You can hide supplier-public fabrics from your public promoter pages without changing the supplier listing.'
                  : 'This fabric is private on the supplier account — it cannot be shown publicly.'}
              </span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={!!fabric.promoterItemPublic}
                onChange={(e) => onTogglePromoterFabricVisibility?.(fabric, e.target.checked)}
                disabled={isVisibilityLoading || !fabric.isPublic}
              />
              <div
                className={`w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200 peer-checked:bg-green-500 ${
                  isVisibilityLoading || !fabric.isPublic ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              />
              <span
                className={`absolute left-0.5 top-0.5 h-4 w-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5 ${
                  isVisibilityLoading || !fabric.isPublic ? 'opacity-60' : ''
                }`}
              />
            </label>
          </div>
        ) : !readOnly ? (
        <div
          className="flex items-center justify-between mb-2 sm:mb-3 text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col">
            <span className="text-gray-500 font-medium">
              {collectionIsPublic ? 'Show on public collection' : 'Make it public'}
            </span>
            {fabric.isPublic ? (
              <span className="text-[10px] text-gray-400">
                {collectionIsPublic ? "Listed on this collection's public page" : 'Visible on your public link'}
              </span>
            ) : collectionIsPublic ? (
              <span className="text-[10px] text-gray-400">Hidden from this collection's public page</span>
            ) : null}
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={!!fabric.isPublic}
              onChange={(e) => onToggleVisibility?.(fabric, e.target.checked)}
              disabled={isVisibilityLoading}
            />
            <div
              className={`w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200 peer-checked:bg-green-500 ${
                isVisibilityLoading ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            ></div>
            <span
              className={`absolute left-0.5 top-0.5 h-4 w-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5 ${
                isVisibilityLoading ? 'opacity-60' : ''
              }`}
            ></span>
          </label>
        </div>
        ) : (
          showReadOnlyHint ? (
            <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 mb-2">
              Read-only: visibility is managed by the collection owner.
            </p>
          ) : null
        )}

        <div className="flex gap-1.5 sm:gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => onGenerate?.(fabric)}
            className="flex-1 bg-blue-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
            title="Generate kit with this fabric"
          >
            <Sparkles size={12} className="sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Generate</span>
            <span className="sm:hidden">Generate</span>
          </button>
          {!readOnly && (
          <>
          {typeof onRemoveFromCollection === 'function' && (
            <button
              type="button"
              onClick={() => onRemoveFromCollection?.(fabric)}
              disabled={isRemovingFromCollection}
              className="px-2 sm:px-3 py-1.5 sm:py-2 bg-amber-50 text-amber-700 rounded-md sm:rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              title="Remove from this collection"
            >
              {isRemovingFromCollection ? (
                <Loader2 size={12} className="sm:w-3.5 sm:h-3.5 animate-spin" />
              ) : (
                <X size={12} className="sm:w-3.5 sm:h-3.5" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => onEdit?.(fabric)}
            className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-100 text-gray-600 rounded-md sm:rounded-lg hover:bg-gray-200 transition-colors"
            title="Edit fabric"
          >
            <Edit3 size={12} className="sm:w-3.5 sm:h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(fabric)}
            className="px-2 sm:px-3 py-1.5 sm:py-2 bg-red-50 text-red-600 rounded-md sm:rounded-lg hover:bg-red-100 transition-colors"
            title="Delete fabric"
          >
            <Trash2 size={12} className="sm:w-3.5 sm:h-3.5" />
          </button>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

const INITIAL_FORM_STATE = {
  name: '',
  properties: [{ key: '', value: '' }],
  selectedFabrics: [],
  imageFile: null,
  imagePreview: null,
  removeImage: false,
  tags: []
};

const FabricCollectionDetailView = ({
  collectionId,
  onBack,
  showNotification,
  onGenerateFabric,
  onEditFabric,
  onDeleteFabric,
  onToggleFabricVisibility,
  openAiStudio,
  onOpenCollection,
  onCollectionVisibilityUpdated,
  onCollectionItemsUpdated,
  readOnly = false,
}) => {
  useBackButton(true, () => { if (onBack) onBack(); });
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formState, setFormState] = useState(INITIAL_FORM_STATE);
  const [saving, setSaving] = useState(false);
  const [availableFabrics, setAvailableFabrics] = useState([]);
  const [fabricPagination, setFabricPagination] = useState({ currentPage: 1, hasNextPage: false });
  const [loadingFabrics, setLoadingFabrics] = useState(false);
  const [previewFabric, setPreviewFabric] = useState(null);
  const [visibilityLoadingId, setVisibilityLoadingId] = useState(null);
  const [removingFabricId, setRemovingFabricId] = useState(null);
  const confirmActionRef = useRef(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    description: '',
    tone: 'danger',
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [fabricSearchTerm, setFabricSearchTerm] = useState('');
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [addItemsTab, setAddItemsTab] = useState('fabrics'); // 'fabrics' | 'collections'
  const [availableChildren, setAvailableChildren] = useState([]);
  const [loadingAvailableChildren, setLoadingAvailableChildren] = useState(false);
  const [addingChildId, setAddingChildId] = useState(null);
  const [removingChildId, setRemovingChildId] = useState(null);
  const [availableAddFabrics, setAvailableAddFabrics] = useState([]);
  const [loadingAddFabrics, setLoadingAddFabrics] = useState(false);
  const [addFabricsPagination, setAddFabricsPagination] = useState({ currentPage: 1, hasNextPage: false });
  const [selectedAddFabricIds, setSelectedAddFabricIds] = useState([]);
  const [addingFabrics, setAddingFabrics] = useState(false);
  const [addItemsSearch, setAddItemsSearch] = useState('');
  const [collectionPublicSaving, setCollectionPublicSaving] = useState(false);
  const [promoterCatalogSaving, setPromoterCatalogSaving] = useState(false);
  const [childPromoterVisibilityLoadingId, setChildPromoterVisibilityLoadingId] = useState(null);
  const fetchingRef = useRef(false); // Track if a fetch is in progress

  const openConfirm = useCallback((config, action) => {
    confirmActionRef.current = action;
    setConfirmModal({
      open: true,
      title: config?.title || 'Are you sure?',
      description: config?.description || '',
      tone: config?.tone || 'danger',
      confirmText: config?.confirmText || 'Confirm',
      cancelText: config?.cancelText || 'Cancel'
    });
  }, []);

  const closeConfirm = useCallback(() => {
    if (confirmLoading) return;
    confirmActionRef.current = null;
    setConfirmModal((prev) => ({ ...prev, open: false }));
  }, [confirmLoading]);

  const runConfirm = useCallback(async () => {
    if (confirmLoading) return;
    const action = confirmActionRef.current;
    if (!action) return;
    setConfirmLoading(true);
    try {
      await action();
      confirmActionRef.current = null;
      setConfirmModal((prev) => ({ ...prev, open: false }));
    } finally {
      setConfirmLoading(false);
    }
  }, [confirmLoading]);

  const fetchCollection = useCallback(async () => {
    if (!collectionId || fetchingRef.current) return; // Prevent concurrent calls
    
    // Check if user is authenticated before making the request
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No authentication token found, skipping fabric collection fetch');
      setError('Authentication required');
      setLoading(false);
      fetchingRef.current = true; // Prevent retries when not authenticated
      return;
    }
    
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    let isUnauthorized = false;
    try {
      const data = await getFabricCollectionById(collectionId, true);
      setCollection(data);
      fetchingRef.current = false; // Reset on success
    } catch (err) {
      console.error('Failed to load fabric collection:', err);
      const status = err?.response?.status;
      const message = err?.response?.data?.message || 'Failed to load fabric collection details.';
      
      // If 401 Unauthorized, don't retry - user needs to authenticate
      if (status === 401) {
        isUnauthorized = true;
        setError('Authentication required. Please log in.');
        showNotification?.('Authentication required to view this collection.', 'error');
        // Keep fetchingRef.current = true to prevent retries
      } else {
        setError(message);
        showNotification?.(message, 'error');
        fetchingRef.current = false; // Allow retry for other errors
      }
    } finally {
      setLoading(false);
      // Keep fetchingRef.current = true for 401 errors to prevent infinite retries
      if (!isUnauthorized && fetchingRef.current) {
        fetchingRef.current = false;
      }
    }
  }, [collectionId]);

  const loadAvailableFabrics = useCallback(async (page = 1, append = false) => {
    setLoadingFabrics(true);
    try {
      const response = await getProducts(page, 30, '', { hasCollection: 'false' });
      const fetched = response?.products || [];
      setAvailableFabrics(prev => append ? [...prev, ...fetched] : fetched);
      setFabricPagination(response?.pagination ? {
        currentPage: response.pagination.currentPage,
        hasNextPage: response.pagination.hasNextPage
      } : { currentPage: 1, hasNextPage: false });
    } catch (err) {
      console.error('Failed to load fabrics for editing:', err);
      showNotification?.('Failed to load fabrics for editing.', 'error');
    } finally {
      setLoadingFabrics(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch if collectionId exists and we're not already fetching
    if (collectionId && !fetchingRef.current) {
      fetchCollection();
    } else if (!collectionId) {
      // Clear collection state when collectionId is removed
      setCollection(null);
      setError(null);
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [collectionId, fetchCollection]); // fetchCollection is stable (only depends on collectionId)

  useEffect(() => {
    setFabricSearchTerm('');
  }, [collection?._id]);

  useEffect(() => {
    if (isEditing) {
      loadAvailableFabrics(1, false);
    }
  }, [isEditing, loadAvailableFabrics]);

  useEffect(() => {
    if (collection?.viewerAccess === 'promoted') {
      setIsEditing(false);
    }
  }, [collection?.viewerAccess]);

  const propertyEntries = useMemo(() => {
    if (!collection?.properties?.length) return [];
    return collection.properties
      .filter(prop => (prop.key || '').trim() || (prop.value || '').trim())
      .map((prop, index) => ({
        id: `${prop.key || 'prop'}-${index}`,
        key: prop.key?.trim(),
        value: prop.value?.trim()
      }));
  }, [collection]);

  const startEditing = () => {
    if (!collection) return;
    if (collection.viewerAccess === 'promoted') return;
    setFormState({
      name: collection.name || '',
      properties: (collection.properties || []).map(prop => ({ key: prop.key || '', value: prop.value || '' })).concat([{ key: '', value: '' }]).slice(0, Math.max(1, (collection.properties || []).length || 1)),
      selectedFabrics: (collection.fabrics || []).map(fabric => fabric._id || fabric),
      imageFile: null,
      imagePreview: collection.image?.url || null,
      removeImage: false,
      tags: (collection.tags || []).map(tag => tag || '').filter(Boolean)
    });
    setTagInput('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setFormState(INITIAL_FORM_STATE);
    setTagInput('');
  };

  const handlePropertyChange = (index, field, value) => {
    setFormState(prev => {
      const nextProps = prev.properties.map((prop, idx) => idx === index ? { ...prop, [field]: value } : prop);
      return { ...prev, properties: nextProps };
    });
  };

  const addProperty = () => {
    setFormState(prev => ({
      ...prev,
      properties: [...prev.properties, { key: '', value: '' }]
    }));
  };

  const removeProperty = (index) => {
    setFormState(prev => {
      if (prev.properties.length === 1) return prev;
      return {
        ...prev,
        properties: prev.properties.filter((_, idx) => idx !== index)
      };
    });
  };

  const handleTagAdd = useCallback(() => {
    const value = tagInput.trim();
    if (!value) return;
    setFormState(prev => {
      if (prev.tags.includes(value) || prev.tags.length >= 30) {
        return prev;
      }
      return {
        ...prev,
        tags: [...prev.tags, value]
      };
    });
    setTagInput('');
  }, [tagInput]);

  const handleTagRemove = useCallback((tagToRemove) => {
    setFormState(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  }, []);

  const handleTagKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      handleTagAdd();
    } else if (event.key === 'Backspace' && !tagInput) {
      setFormState(prev => ({
        ...prev,
        tags: prev.tags.slice(0, -1)
      }));
    }
  }, [handleTagAdd, tagInput]);

  const toggleFabricSelection = (fabricId) => {
    setFormState(prev => {
      const exists = prev.selectedFabrics.includes(fabricId);
      const selectedFabrics = exists
        ? prev.selectedFabrics.filter(id => id !== fabricId)
        : [...prev.selectedFabrics, fabricId];
      return {
        ...prev,
        selectedFabrics
      };
    });
  };

  const handleCatalogueFabricUpdated = useCallback((updatedFabric) => {
    if (!updatedFabric?._id) return;

    setCollection(prev => {
      if (!prev) return prev;

      const updatedFabrics = (prev.fabrics || []).map(fabric =>
        fabric._id === updatedFabric._id
          ? { ...fabric, ...updatedFabric, user: updatedFabric.user ?? fabric.user }
          : fabric
      );

      return { ...prev, fabrics: updatedFabrics };
    });

    setPreviewFabric(prev =>
      prev?._id === updatedFabric._id
        ? { ...prev, ...updatedFabric, user: updatedFabric.user ?? prev.user }
        : prev
    );
  }, []);

  const handleImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const previewUrl = typeof window !== 'undefined' ? URL.createObjectURL(file) : null;
    setFormState(prev => {
      if (prev.imagePreview && typeof window !== 'undefined' && prev.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(prev.imagePreview);
      }

      return {
        ...prev,
        imageFile: file,
        imagePreview: previewUrl,
        removeImage: false
      };
    });
  };

  const handleImageClear = () => {
    if (formState.imagePreview && typeof window !== 'undefined' && formState.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(formState.imagePreview);
    }

    setFormState(prev => ({
      ...prev,
      imageFile: null,
      imagePreview: null,
      removeImage: true
    }));
  };

  useEffect(() => {
    return () => {
      if (formState.imagePreview && typeof window !== 'undefined' && formState.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(formState.imagePreview);
      }
    };
  }, [formState.imagePreview]);

  const handleSave = async () => {
    if (!collection?._id) return;
    if (!formState.name.trim()) {
      showNotification?.('Please enter a collection name.', 'error');
      return;
    }

    const preparedProperties = formState.properties
      .filter(prop => (prop.key || '').trim() || (prop.value || '').trim())
      .map(prop => ({ key: prop.key.trim(), value: prop.value.trim() }));

    setSaving(true);
    try {
      const payload = {
        name: formState.name.trim(),
        properties: preparedProperties,
        fabrics: formState.selectedFabrics,
        tags: formState.tags
      };

      if (formState.imageFile) {
        payload.imageFile = formState.imageFile;
      } else if (formState.removeImage) {
        payload.removeImage = true;
      }

      await updateFabricCollection(collection._id, payload);
      showNotification?.('Fabric collection updated.', 'success');
      setIsEditing(false);
      await fetchCollection();
    } catch (err) {
      console.error('Failed to update collection:', err);
      const message = err?.response?.data?.message || 'Failed to update fabric collection.';
      showNotification?.(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!collection?._id) return;
    openConfirm(
      {
        title: `Delete "${collection.name}"?`,
        description: 'This will delete the collection. Fabrics will remain, but their collection link will be cleared.',
        tone: 'danger',
        confirmText: 'Delete'
      },
      async () => {
        await deleteFabricCollection(collection._id);
        showNotification?.('Fabric collection deleted.', 'success');
        onBack?.();
      }
    );
  };

  const handleCollectionPublicToggle = useCallback(
    async (nextPublic) => {
      if (!collection?._id || collectionPublicSaving) return;
      setCollectionPublicSaving(true);
      try {
        await updateFabricCollection(collection._id, { isPublic: nextPublic });
        showNotification?.(
          nextPublic
            ? 'Collection is public. Fabrics are public by default; turn any fabric off below to keep it private.'
            : 'Collection is private. Fabrics in this collection are made private automatically.',
          'success'
        );
        onCollectionVisibilityUpdated?.({ collectionId: collection._id, isPublic: nextPublic });
        await fetchCollection();
      } catch (err) {
        console.error('Failed to update collection visibility:', err);
        const message = err?.response?.data?.message || 'Failed to update collection visibility.';
        showNotification?.(message, 'error');
      } finally {
        setCollectionPublicSaving(false);
      }
    },
    [collection?._id, collectionPublicSaving, fetchCollection, onCollectionVisibilityUpdated, showNotification]
  );

  const handleFabricVisibilityToggle = useCallback(
    async (fabric, makePublic) => {
      if (!onToggleFabricVisibility) return;

      setVisibilityLoadingId(fabric?._id || null);
      try {
        const updatedProduct = await onToggleFabricVisibility(fabric, makePublic);
        setCollection(prev => {
          if (!prev?.fabrics) return prev;
          const nextFabrics = prev.fabrics.map(item => {
            if (item._id !== fabric._id) return item;
            const nextVisibility =
              updatedProduct?.isPublic !== undefined ? updatedProduct.isPublic : makePublic;
            return {
              ...item,
              ...updatedProduct,
              isPublic: nextVisibility,
              publicAt:
                updatedProduct?.publicAt !== undefined
                  ? updatedProduct.publicAt
                  : nextVisibility
                    ? new Date().toISOString()
                    : null
            };
          });
          return { ...prev, fabrics: nextFabrics };
        });
      } catch (toggleError) {
        console.error('Failed to toggle fabric visibility:', toggleError);
      } finally {
        setVisibilityLoadingId(null);
      }
    },
    [onToggleFabricVisibility]
  );

  const handlePromoterFabricVisibilityToggle = useCallback(
    async (fabric, makePublic) => {
      if (!collection?._id || !fabric?._id) return;
      setVisibilityLoadingId(fabric._id);
      try {
        const data = await updatePromoterFabricVisibility(collection._id, {
          fabricId: fabric._id,
          isPublicStatus: makePublic,
        });
        const nextPublic = data?.promoterItemPublic === true;
        setCollection((prev) => {
          if (!prev?.fabrics) return prev;
          return {
            ...prev,
            fabrics: prev.fabrics.map((item) =>
              item._id === fabric._id ? { ...item, promoterItemPublic: nextPublic } : item
            ),
          };
        });
        showNotification?.('Visibility updated.', 'success');
      } catch (err) {
        const message = err?.response?.data?.message || 'Failed to update visibility.';
        showNotification?.(message, 'error');
      } finally {
        setVisibilityLoadingId(null);
      }
    },
    [collection?._id, showNotification]
  );

  const handlePromoterCollectionCatalogToggle = useCallback(
    async (makePublic) => {
      if (!collection?._id || promoterCatalogSaving) return;
      setPromoterCatalogSaving(true);
      try {
        await updatePromotedCollectionVisibility(collection._id, makePublic);
        setCollection((prev) => ({ ...prev, promoterVisibility: makePublic }));
        showNotification?.(
          makePublic
            ? 'This supplier collection will appear on your public promoter catalogue.'
            : 'This supplier collection is hidden from your public promoter catalogue.',
          'success'
        );
        await fetchCollection();
      } catch (err) {
        const message = err?.response?.data?.message || 'Failed to update catalogue visibility.';
        showNotification?.(message, 'error');
      } finally {
        setPromoterCatalogSaving(false);
      }
    },
    [collection?._id, promoterCatalogSaving, fetchCollection, showNotification]
  );

  const handleChildCollectionPromoterVisibility = useCallback(
    async (child, makePublic) => {
      if (!child?._id) return;
      setChildPromoterVisibilityLoadingId(child._id);
      try {
        await updatePromotedCollectionVisibility(child._id, makePublic);
        setCollection((prev) => ({
          ...prev,
          childCollections: (prev.childCollections || []).map((c) =>
            c._id === child._id ? { ...c, promoterVisibility: makePublic } : c
          ),
        }));
        showNotification?.('Visibility updated.', 'success');
      } catch (err) {
        const message = err?.response?.data?.message || 'Failed to update visibility.';
        showNotification?.(message, 'error');
      } finally {
        setChildPromoterVisibilityLoadingId(null);
      }
    },
    [showNotification]
  );

  const loadAvailableChildren = useCallback(async () => {
    if (!collectionId) return;
    setLoadingAvailableChildren(true);
    try {
      const list = await getAvailableChildCollections(collectionId);
      setAvailableChildren(list);
    } catch (err) {
      console.error('Failed to load available collections:', err);
      showNotification?.('Failed to load collections to add.', 'error');
      setAvailableChildren([]);
    } finally {
      setLoadingAvailableChildren(false);
    }
  }, [collectionId]);

  const handleAddCollectionAsChild = useCallback(async (childCollectionId) => {
    if (!collectionId) return;
    setAddingChildId(childCollectionId);
    try {
      await addCollectionAsChild(collectionId, childCollectionId);
      showNotification?.('Collection added as child.', 'success');
      setShowAddItemsModal(false);
      await fetchCollection();
      onCollectionItemsUpdated?.();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to add collection as child.';
      showNotification?.(msg, 'error');
    } finally {
      setAddingChildId(null);
    }
  }, [collectionId, fetchCollection, onCollectionItemsUpdated, showNotification]);

  const handleRemoveFromParent = useCallback(async (childCollectionId) => {
    openConfirm(
      {
        title: 'Remove collection from parent?',
        description: 'This will detach the child collection and make it a root-level collection.',
        tone: 'danger',
        confirmText: 'Remove'
      },
      async () => {
        setRemovingChildId(childCollectionId);
        try {
          await removeCollectionFromParent(childCollectionId, collectionId);
          showNotification?.('Collection removed from parent.', 'success');
          await fetchCollection();
          onCollectionItemsUpdated?.();
        } catch (err) {
          const msg = err?.response?.data?.message || 'Failed to remove from parent.';
          showNotification?.(msg, 'error');
        } finally {
          setRemovingChildId(null);
        }
      }
    );
  }, [collectionId, fetchCollection, onCollectionItemsUpdated, openConfirm, showNotification]);

  const handleRemoveFabricFromCollection = useCallback(
    async (fabric) => {
      const fabricId = fabric?._id;
      if (!collection?._id || !fabricId) return;
      openConfirm(
        {
          title: 'Remove fabric from collection?',
          description: `This will remove "${fabric?.name || 'this fabric'}" from the collection.`,
          tone: 'danger',
          confirmText: 'Remove'
        },
        async () => {
          setRemovingFabricId(fabricId);
          try {
            const existingIds = (collection.fabrics || [])
              .map((f) => (f?._id || f)?.toString())
              .filter(Boolean);
            const nextIds = existingIds.filter((id) => id !== String(fabricId));
            await updateFabricCollection(collection._id, { fabrics: nextIds });
            showNotification?.('Fabric removed from collection.', 'success');
            await fetchCollection();
            onCollectionItemsUpdated?.();
          } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to remove fabric from collection.';
            showNotification?.(msg, 'error');
          } finally {
            setRemovingFabricId(null);
          }
        }
      );
    },
    [collection?._id, collection?.fabrics, fetchCollection, onCollectionItemsUpdated, openConfirm, showNotification]
  );

  const loadAvailableAddFabrics = useCallback(
    async (page = 1, append = false) => {
      setLoadingAddFabrics(true);
      try {
        const search = (addItemsSearch || '').trim();
        // Only show fabrics not already assigned to a collection (current system constraint).
        const response = await getProducts(page, 30, search, { hasCollection: 'false' });
        const fetched = response?.products || [];
        setAvailableAddFabrics((prev) => (append ? [...prev, ...fetched] : fetched));
        setAddFabricsPagination(
          response?.pagination
            ? {
                currentPage: response.pagination.currentPage,
                hasNextPage: response.pagination.hasNextPage,
              }
            : { currentPage: 1, hasNextPage: false }
        );
      } catch (err) {
        console.error('Failed to load fabrics to add:', err);
        showNotification?.('Failed to load fabrics to add.', 'error');
        setAvailableAddFabrics([]);
      } finally {
        setLoadingAddFabrics(false);
      }
    },
    [addItemsSearch]
  );

  const toggleAddFabricSelection = useCallback((fabricId) => {
    setSelectedAddFabricIds((prev) => {
      const id = String(fabricId);
      return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    });
  }, []);

  const handleAddSelectedFabrics = useCallback(async () => {
    if (!collection?._id) return;
    if (selectedAddFabricIds.length === 0) return;
    setAddingFabrics(true);
    try {
      const existingIds = (collection.fabrics || [])
        .map((f) => (f?._id || f)?.toString())
        .filter(Boolean);
      const nextIds = Array.from(new Set([...existingIds, ...selectedAddFabricIds]));
      await updateFabricCollection(collection._id, { fabrics: nextIds });
      showNotification?.('Fabrics added to collection.', 'success');
      setShowAddItemsModal(false);
      setSelectedAddFabricIds([]);
      await fetchCollection();
      onCollectionItemsUpdated?.();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to add fabrics to collection.';
      showNotification?.(msg, 'error');
    } finally {
      setAddingFabrics(false);
    }
  }, [collection?._id, collection?.fabrics, fetchCollection, onCollectionItemsUpdated, selectedAddFabricIds, showNotification]);

  const handleOpenAddItemsModal = useCallback(() => {
    setShowAddItemsModal(true);
    setAddItemsTab('fabrics');
    setSelectedAddFabricIds([]);
    setAddItemsSearch('');
    loadAvailableAddFabrics(1, false);
    loadAvailableChildren();
  }, [loadAvailableAddFabrics, loadAvailableChildren]);

  useEffect(() => {
    if (!showAddItemsModal) return;
    if (addItemsTab === 'fabrics') {
      const t = setTimeout(() => {
        loadAvailableAddFabrics(1, false);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [addItemsSearch, addItemsTab, showAddItemsModal, loadAvailableAddFabrics]);

  const filteredCollectionFabrics = useMemo(() => {
    const fabrics = collection?.fabrics || [];
    const query = fabricSearchTerm.trim().toLowerCase();
    if (!query) return fabrics;
    return fabrics.filter(fabric => {
      const targetStrings = [
        fabric?.name,
        fabric?.description,
        ...(Array.isArray(fabric?.tags) ? fabric.tags : []),
        fabric?.fabricTexture?.name
      ]
        .filter(Boolean)
        .map(value => String(value).toLowerCase());

      return targetStrings.some(value => value.includes(query));
    });
  }, [collection?.fabrics, fabricSearchTerm]);

  const totalFabrics = collection?.fabrics?.length || 0;
  const visibleFabricsCount = filteredCollectionFabrics.length;
  const isPromotedViewer = collection?.viewerAccess === 'promoted';
  const isMutableView = !readOnly && !isPromotedViewer;
  const isFabricCardReadOnly = readOnly || isPromotedViewer;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-hidden">
      <div className="h-full w-full flex flex-col bg-white">
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 sm:px-6 pb-4 pt-[max(1rem,env(safe-area-inset-top))] flex items-center justify-between shadow-lg">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <button
              onClick={onBack}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-white/20 transition-colors active:scale-95"
              title="Go back"
              aria-label="Go back"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">{collection?.name || 'Fabric Collection'}</h1>
              <p className="text-sm text-purple-100">{readOnly ? 'Collection Detail' : 'Fabric Collection Overview'}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs sm:text-sm text-purple-100">
            {collection?.fabrics?.length || 0} fabrics grouped
            {isMutableView && (
            <div className="flex items-center gap-2">
              <button
                className="inline-flex items-center gap-1 px-3 py-1 bg-white/15 hover:bg-white/25 rounded-md text-white"
                onClick={isEditing ? cancelEditing : startEditing}
              >
                {isEditing ? <><X size={14} />Cancel</> : <><Edit3 size={14} />Edit</>}
              </button>
            </div>
            )}
            {isMutableView && (
            <button
              className="inline-flex items-center gap-1 px-3 py-1 bg-red-500/80 hover:bg-red-600 rounded-md text-white"
              onClick={handleDelete}
            >
              <Trash2 size={14} /> Delete
            </button>
            )}
            {isPromotedViewer && (
              <span className="text-[11px] sm:text-xs bg-white/15 px-2 py-1 rounded-md max-w-xs sm:max-w-md">
                Promoter view — edit catalogue visibility for fabrics and nested collections
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
          <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={32} className="animate-spin text-purple-600" />
              </div>
            ) : error ? (
              <div className="bg-white border border-red-200 text-red-600 rounded-xl p-6 shadow">
                {error}
              </div>
            ) : !collection ? (
              <div className="bg-white border border-gray-200 text-gray-500 rounded-xl p-6 shadow">
                Collection not found.
              </div>
            ) : (
              <div className="space-y-6">
                {!readOnly && (
                <div className="bg-white rounded-2xl shadow-lg border border-purple-100 overflow-hidden">
                      <div className="relative aspect-video w-full bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50">
                        {isEditing ? (
                          <div className="absolute inset-0">
                            <div className="flex-1 border-2 border-dashed border-white/40 rounded-xl bg-black/20 flex items-center justify-center p-4 overflow-hidden max-h-[360px]">
                              <input
                                type="file"
                                accept="image/*"
                                id="collection-detail-image-upload"
                                className="hidden"
                                onChange={handleImageSelect}
                              />
                              <label
                                htmlFor="collection-detail-image-upload"
                                className="w-full h-full flex flex-col items-center justify-center cursor-pointer space-y-3 text-white"
                              >
                                {formState.imagePreview ? (
                                  <div className="relative w-full h-full max-h-[360px]">
                                    <div className="absolute inset-0 rounded-xl border-2 border-white/30 pointer-events-none" />
                                    <img
                                      src={formState.imagePreview}
                                      alt={collection.name}
                                      className="w-full h-full object-contain rounded-lg shadow-lg bg-black/30"
                                    />
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleImageClear();
                                      }}
                                      className="absolute top-3 right-3 px-3 py-1.5 bg-red-500/90 hover:bg-red-600 text-xs font-semibold text-white rounded-full shadow transition-colors flex items-center gap-1"
                                    >
                                      <Trash2 size={12} />
                                      Remove
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <div className="w-16 h-16 bg-white/30 rounded-full flex items-center justify-center">
                                      <ImageIcon size={28} />
                                    </div>
                                    <p className="text-sm font-medium">Click to upload image</p>
                                    <p className="text-xs text-white/70">PNG, JPG, GIF up to 10MB</p>
                                  </>
                                )}
                              </label>
                            </div>
                          </div>
                        ) : collection.image?.url ? (
                          <div className="w-full h-full bg-gradient-to-br from-purple-900/10 via-purple-900/5 to-indigo-900/10 flex items-center justify-center">
                            <ThumbnailImg
                              src={collection.image.url}
                              alt={collection.name}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full px-6">
                            <span className="text-2xl sm:text-3xl font-bold text-purple-600 text-center break-words">
                              {collection.name}
                            </span>
                          </div>
                        )}
                        <div className="absolute top-3 left-3 bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                          Collection
                        </div>
                        <div className="absolute top-3 right-3 bg-pink-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow flex items-center gap-1">
                          <Sparkles size={14} />
                          {collection.fabrics?.length || 0} fabrics
                        </div>
                      </div>
                  <div className="p-5 sm:p-6 space-y-4">
                    <div className="flex flex-col gap-3">
                      {isEditing ? (
                        <input
                          value={formState.name}
                          onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
                          className="text-2xl sm:text-3xl font-semibold text-gray-900 border-b border-purple-300 focus:outline-none focus:border-purple-500"
                        />
                      ) : (
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{collection.name}</h2>
                      )}
                      {!isEditing && (
                        <p className="text-sm text-gray-500">
                          Created {new Date(collection.createdAt).toLocaleString()}
                        </p>
                      )}
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                        <h3 className="text-sm font-semibold text-purple-700 mb-3">Collection Properties</h3>
                        {isEditing ? (
                          <div className="space-y-3">
                            {formState.properties.map((property, index) => (
                              <div key={`edit-property-${index}`} className="flex gap-2 items-start">
                                <div className="flex-1 space-y-2">
                                  <input
                                    value={property.key}
                                    onChange={(e) => handlePropertyChange(index, 'key', e.target.value)}
                                    placeholder="Key"
                                    className="w-full border border-purple-200 rounded-md px-3 py-2 text-sm focus:border-purple-400 focus:ring-2 focus:ring-purple-200"
                                  />
                                  <input
                                    value={property.value}
                                    onChange={(e) => handlePropertyChange(index, 'value', e.target.value)}
                                    placeholder="Value"
                                    className="w-full border border-purple-200 rounded-md px-3 py-2 text-sm focus:border-purple-400 focus:ring-2 focus:ring-purple-200"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeProperty(index)}
                                  className="mt-1 text-red-500 hover:text-red-600"
                                  disabled={formState.properties.length === 1}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={addProperty}
                              className="inline-flex items-center gap-2 text-xs text-purple-600 font-semibold"
                            >
                              <Plus size={14} /> Add property
                            </button>
                          </div>
                        ) : propertyEntries.length > 0 ? (
                          <dl className="space-y-2">
                            {propertyEntries.map(entry => (
                              <div key={entry.id} className="flex flex-col">
                                <dt className="text-xs uppercase tracking-wide text-purple-500">{entry.key || 'Property'}</dt>
                                <dd className="text-sm text-purple-900">{entry.value || '—'}</dd>
                              </div>
                            ))}
                          </dl>
                        ) : (
                          <p className="text-sm text-purple-600">No custom properties added yet.</p>
                        )}
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                        <h3 className="text-sm font-semibold text-blue-700 mb-3">Quick Summary</h3>
                        <ul className="space-y-2 text-sm text-blue-900">
                          {isMutableView && (
                          <li
                            className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-3 mb-1 border-b border-blue-100/80"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-start gap-2 min-w-0">
                              <Globe2 size={18} className="text-blue-500 shrink-0 mt-0.5" aria-hidden />
                              <div>
                                <span className="font-semibold text-blue-900">
                                  {isPromotedViewer ? 'Your promoter catalogue' : 'Public collection'}
                                </span>
                                <p className="text-xs text-blue-800/80 mt-1 leading-snug">
                                  {isPromotedViewer
                                    ? 'Hide or show this supplier collection on your public promoter pages. You can also control each fabric and nested collection below.'
                                    : 'When enabled, this collection appears on your public link and fabrics here are public unless you turn them off below.'}
                                </p>
                              </div>
                            </div>
                            {isPromotedViewer ? (
                              <label className="relative inline-flex items-center cursor-pointer shrink-0 self-end sm:self-center">
                                <input
                                  type="checkbox"
                                  className="sr-only peer"
                                  checked={!!collection.promoterVisibility}
                                  onChange={(e) => handlePromoterCollectionCatalogToggle(e.target.checked)}
                                  disabled={promoterCatalogSaving}
                                />
                                <div
                                  className={`w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200 peer-checked:bg-green-500 ${
                                    promoterCatalogSaving ? 'opacity-60 cursor-not-allowed' : ''
                                  }`}
                                />
                                <span
                                  className={`absolute left-0.5 top-0.5 h-4 w-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5 ${
                                    promoterCatalogSaving ? 'opacity-60' : ''
                                  }`}
                                />
                              </label>
                            ) : (
                              <label className="relative inline-flex items-center cursor-pointer shrink-0 self-end sm:self-center">
                                <input
                                  type="checkbox"
                                  className="sr-only peer"
                                  checked={!!collection.isPublic}
                                  onChange={(e) => handleCollectionPublicToggle(e.target.checked)}
                                  disabled={collectionPublicSaving}
                                />
                                <div
                                  className={`w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200 peer-checked:bg-green-500 ${
                                    collectionPublicSaving ? 'opacity-60 cursor-not-allowed' : ''
                                  }`}
                                />
                                <span
                                  className={`absolute left-0.5 top-0.5 h-4 w-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5 ${
                                    collectionPublicSaving ? 'opacity-60' : ''
                                  }`}
                                />
                              </label>
                            )}
                          </li>
                          )}
                          <li className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                            {collection.fabrics?.length || 0} fabric material{collection.fabrics?.length === 1 ? '' : 's'} grouped.
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                            {propertyEntries.length || 0} property tag{propertyEntries.length === 1 ? '' : 's'} defined.
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                            Last updated {new Date(collection.updatedAt).toLocaleString()}
                          </li>
                        </ul>
                      </div>
                    </div>

                    {isEditing ? (
                      <div className="bg-white rounded-xl border border-purple-100 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-purple-700">Collection Tags</span>
                          <span className="text-xs text-gray-500">{formState.tags.length}/30</span>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <input
                            type="text"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={handleTagKeyDown}
                            placeholder="Add tags (e.g., festive, premium, silk)"
                            className="w-full sm:flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                          />
                          <button
                            type="button"
                            onClick={handleTagAdd}
                            className="w-full sm:w-auto px-4 py-2.5 bg-purple-500 text-white rounded-lg text-sm font-semibold hover:bg-purple-600 transition-colors"
                          >
                            Add Tag
                          </button>
                        </div>
                        {formState.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {formState.tags.map(tag => (
                              <span
                                key={tag}
                                className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-medium"
                              >
                                #{tag}
                                <button
                                  type="button"
                                  onClick={() => handleTagRemove(tag)}
                                  className="text-purple-500 hover:text-purple-700"
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">
                            Add descriptive tags to improve searchability.
                          </p>
                        )}
                      </div>
                    ) : collection?.tags?.length ? (
                      <div className="flex flex-wrap gap-2">
                        {collection.tags.map(tag => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-3 py-1 bg-purple-50 text-purple-600 rounded-full text-xs font-medium"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
                )}

                {isEditing ? (
                  <section className="bg-white rounded-2xl border border-purple-100 shadow-lg p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Select Fabrics to include</h2>
                      <span className="text-sm text-gray-500">{formState.selectedFabrics.length} selected</span>
                    </div>
                    <div className="max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                      {loadingFabrics ? (
                        <div className="flex justify-center py-6 text-purple-600">
                          <Loader2 size={20} className="animate-spin" />
                        </div>
                      ) : availableFabrics.length === 0 ? (
                        <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 text-sm text-purple-600">
                          All fabrics are currently assigned to collections. Add new fabrics to include them here.
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {availableFabrics.map(fabric => {
                            const image = getFabricImage(fabric);
                            const isSelected = formState.selectedFabrics.includes(fabric._id);
                            return (
                              <button
                                type="button"
                                key={fabric._id}
                                onClick={() => toggleFabricSelection(fabric._id)}
                                className={`group border rounded-lg overflow-hidden text-left transition-all ${
                                  isSelected ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200 hover:border-purple-300'
                                }`}
                              >
                                <div className="relative aspect-square bg-gray-100 overflow-hidden">
                                  <ThumbnailImg
                                    src={image}
                                    alt={fabric.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                  />
                                  {isSelected && (
                                    <div className="absolute inset-0 bg-purple-600/30 flex items-center justify-center">
                                      <span className="bg-white text-purple-600 text-xs font-semibold px-2 py-1 rounded-full">
                                        Selected
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="p-2">
                                  <h4 className="text-xs font-semibold text-gray-800 line-clamp-1">{fabric.name}</h4>
                                  <p className="text-[11px] text-gray-500 line-clamp-2">
                                    {fabric.description || 'No description'}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {fabricPagination.hasNextPage && (
                        <div className="flex justify-center py-3">
                          <button
                            type="button"
                            className="text-xs font-semibold text-purple-600 hover:text-purple-700"
                            onClick={() => loadAvailableFabrics(fabricPagination.currentPage + 1, true)}
                          >
                            {loadingFabrics ? 'Loading…' : 'Load more fabrics'}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={cancelEditing}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-60"
                      >
                        {saving ? <Loader2 size={18} className="animate-spin" /> : <Edit3 size={18} />}
                        <span>{saving ? 'Saving…' : 'Update Collection'}</span>
                      </button>
                    </div>
                  </section>
                ) : (
                  <section className="bg-white rounded-2xl border border-purple-100 shadow-lg p-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                          Items in this collection
                        </h2>
                        <span className="text-sm text-gray-500">
                          ({(collection?.childCollections?.length || 0) + (visibleFabricsCount || 0)})
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <div className="relative">
                          <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                          />
                          <input
                            type="text"
                            value={fabricSearchTerm}
                            onChange={(event) => setFabricSearchTerm(event.target.value)}
                            placeholder="Search items..."
                            className="w-full sm:w-64 pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                          />
                        </div>
                        {!isMutableView ? null : (
                          <button
                            type="button"
                            onClick={handleOpenAddItemsModal}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                          >
                            <Plus size={18} />
                            Add items
                          </button>
                        )}
                      </div>
                    </div>

                    {((collection?.childCollections?.length || 0) + (visibleFabricsCount || 0)) === 0 ? (
                      <div className="bg-white border border-gray-200 rounded-xl p-6 text-gray-500 text-sm shadow-sm">
                        No items linked to this collection yet.
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                        {(collection?.childCollections || [])
                          .filter((child) => {
                            const q = fabricSearchTerm.trim().toLowerCase();
                            if (!q) return true;
                            return String(child?.name || '').toLowerCase().includes(q);
                          })
                          .map((child) => {
                            const childImage =
                              child.image?.url ||
                              (child.fabrics?.[0] && getFabricImage(child.fabrics[0])) ||
                              'https://via.placeholder.com/200x200?text=Collection';
                            const fabricCount = child.fabrics?.length || 0;
                            return (
                              <div
                                key={child._id}
                                className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-purple-300 hover:shadow-lg transition-all cursor-pointer"
                              >
                                <div
                                  className="relative aspect-square bg-gray-100"
                                  onClick={() => onOpenCollection?.(child._id)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      onOpenCollection?.(child._id);
                                    }
                                  }}
                                >
                                  <img
                                    src={childImage}
                                    alt={child.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                  />
                                  <div className="absolute top-2 left-2 bg-purple-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                                    Collection
                                  </div>
                                  {fabricCount > 0 && (
                                    <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                                      {fabricCount} fabric{fabricCount === 1 ? '' : 's'}
                                    </div>
                                  )}
                                </div>
                                <div className="p-3">
                                  <h4 className="font-semibold text-gray-800 line-clamp-1 text-sm mb-1">
                                    {child.name}
                                  </h4>
                                  {typeof child.promoterVisibility === 'boolean' && (
                                    <div
                                      className="flex items-center justify-between gap-2 mb-2 text-[11px]"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <span className="text-gray-500">Show in my catalogue</span>
                                      <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                        <input
                                          type="checkbox"
                                          className="sr-only peer"
                                          checked={!!child.promoterVisibility}
                                          onChange={(e) =>
                                            handleChildCollectionPromoterVisibility(child, e.target.checked)
                                          }
                                          disabled={childPromoterVisibilityLoadingId === child._id}
                                        />
                                        <div
                                          className={`w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200 peer-checked:bg-green-500 ${
                                            childPromoterVisibilityLoadingId === child._id
                                              ? 'opacity-60 cursor-not-allowed'
                                              : ''
                                          }`}
                                        />
                                        <span
                                          className={`absolute left-0.5 top-0.5 h-4 w-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5 ${
                                            childPromoterVisibilityLoadingId === child._id ? 'opacity-60' : ''
                                          }`}
                                        />
                                      </label>
                                    </div>
                                  )}
                                  {isMutableView && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveFromParent(String(child._id));
                                      }}
                                      disabled={removingChildId === child._id}
                                      className="text-xs text-red-500 hover:text-red-600 font-medium disabled:opacity-50"
                                    >
                                      {removingChildId === child._id ? 'Removing…' : 'Remove from parent'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                        {filteredCollectionFabrics.map((fabric) => (
                          <FabricPreviewCard
                            key={fabric._id}
                            fabric={fabric}
                            onOpen={setPreviewFabric}
                            onGenerate={onGenerateFabric}
                            onEdit={onEditFabric}
                            onDelete={onDeleteFabric}
                            onRemoveFromCollection={isMutableView ? handleRemoveFabricFromCollection : undefined}
                            isRemovingFromCollection={String(removingFabricId) === String(fabric._id)}
                            onToggleVisibility={isMutableView ? handleFabricVisibilityToggle : undefined}
                            onTogglePromoterFabricVisibility={
                              isPromotedViewer ? handlePromoterFabricVisibilityToggle : undefined
                            }
                            isVisibilityLoading={visibilityLoadingId === fabric._id}
                            collectionIsPublic={!!collection.isPublic}
                            readOnly={isFabricCardReadOnly}
                            showReadOnlyHint={!readOnly}
                            promoterFabricMode={isPromotedViewer}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {previewFabric && (
        <FabricCatalogueView
          fabric={previewFabric}
          liteMode={readOnly}
          onClose={() => setPreviewFabric(null)}
          showNotification={showNotification}
          onFabricUpdated={handleCatalogueFabricUpdated}
          onGenerateFabric={onGenerateFabric}
        />
      )}

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        description={confirmModal.description}
        tone={confirmModal.tone}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        isLoading={confirmLoading}
        onCancel={closeConfirm}
        onConfirm={runConfirm}
      />

      {/* Add Items Modal */}
      {showAddItemsModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Add items to this collection</h3>
                <p className="text-xs text-gray-500 mt-0.5">Add fabrics or add an existing collection as a child.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowAddItemsModal(false)}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-4 pt-4">
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                <button
                  type="button"
                  onClick={() => setAddItemsTab('fabrics')}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${
                    addItemsTab === 'fabrics' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Fabrics
                </button>
                <button
                  type="button"
                  onClick={() => setAddItemsTab('collections')}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${
                    addItemsTab === 'collections' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Collections
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              {addItemsTab === 'fabrics' ? (
                <div className="space-y-3">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={addItemsSearch}
                      onChange={(e) => setAddItemsSearch(e.target.value)}
                      placeholder="Search fabrics to add..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                    />
                  </div>

                  <p className="text-xs text-gray-500">
                    Only fabrics not already assigned to a collection can be added here.
                  </p>

                  {loadingAddFabrics ? (
                    <div className="flex justify-center py-8">
                      <Loader2 size={24} className="animate-spin text-purple-600" />
                    </div>
                  ) : availableAddFabrics.length === 0 ? (
                    <p className="text-gray-500 text-center py-6">
                      No fabrics available to add.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {availableAddFabrics.map((fabric) => {
                        const image = getFabricImage(fabric);
                        const id = String(fabric._id);
                        const isSelected = selectedAddFabricIds.includes(id);
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => toggleAddFabricSelection(id)}
                            className={`text-left border rounded-lg overflow-hidden hover:shadow-md transition-all ${
                              isSelected ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200 hover:border-purple-400'
                            }`}
                          >
                            <div className="relative aspect-square bg-gray-100">
                              <ThumbnailImg
                                src={image}
                                alt={fabric.name}
                                className="w-full h-full object-cover"
                              />
                              {isSelected && (
                                <div className="absolute inset-0 bg-purple-600/25 flex items-center justify-center">
                                  <span className="bg-white text-purple-700 text-xs font-semibold px-2 py-1 rounded-full">
                                    Selected
                                  </span>
                                </div>
                              )}
                              <span className="absolute top-2 left-2 bg-indigo-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                                Fabric
                              </span>
                            </div>
                            <div className="p-2">
                              <p className="font-medium text-gray-800 text-sm line-clamp-1">{fabric.name}</p>
                              <p className="text-xs text-gray-500 line-clamp-2">{fabric.description || 'No description'}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {addFabricsPagination.hasNextPage && (
                    <div className="flex justify-center pt-2">
                      <button
                        type="button"
                        className="text-xs font-semibold text-purple-600 hover:text-purple-700"
                        disabled={loadingAddFabrics}
                        onClick={() => loadAvailableAddFabrics(addFabricsPagination.currentPage + 1, true)}
                      >
                        {loadingAddFabrics ? 'Loading…' : 'Load more fabrics'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {loadingAvailableChildren ? (
                    <div className="flex justify-center py-8">
                      <Loader2 size={24} className="animate-spin text-purple-600" />
                    </div>
                  ) : availableChildren.length === 0 ? (
                    <p className="text-gray-500 text-center py-6">
                      No other collections available to add. Create more collections first.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {availableChildren.map((c) => {
                        const img =
                          c.image?.url ||
                          (c.fabrics?.[0] && getFabricImage(c.fabrics[0])) ||
                          'https://via.placeholder.com/150x150?text=Collection';
                        const count = c.fabrics?.length || 0;
                        const isAdding = addingChildId === c._id;
                        return (
                          <button
                            key={c._id}
                            type="button"
                            onClick={() => handleAddCollectionAsChild(c._id)}
                            disabled={!!addingChildId}
                            className="text-left border border-gray-200 rounded-lg overflow-hidden hover:border-purple-400 hover:shadow-md transition-all disabled:opacity-60"
                          >
                            <div className="aspect-square bg-gray-100 relative">
                              <ThumbnailImg
                                src={img}
                                alt={c.name}
                                className="w-full h-full object-cover"
                              />
                              <span className="absolute top-2 left-2 bg-purple-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                                Collection
                              </span>
                              {count > 0 && (
                                <span className="absolute top-1 right-1 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  {count} fabric{count === 1 ? '' : 's'}
                                </span>
                              )}
                            </div>
                            <div className="p-2">
                              <p className="font-medium text-gray-800 text-sm line-clamp-1">{c.name}</p>
                              {isAdding && (
                                <span className="text-xs text-purple-600 flex items-center gap-1 mt-1">
                                  <Loader2 size={12} className="animate-spin" />
                                  Adding…
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {addItemsTab === 'fabrics' && (
              <div className="p-4 border-t border-gray-200 flex items-center justify-between gap-3">
                <span className="text-sm text-gray-600">
                  {selectedAddFabricIds.length} selected
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddItemsModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    disabled={addingFabrics}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddSelectedFabrics}
                    disabled={addingFabrics || selectedAddFabricIds.length === 0}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {addingFabrics ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                    Add selected fabrics
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FabricCollectionDetailView;

